/**
 * POZITIILE SALVATE ale hartii ADM, puse inapoi pe desen.
 *
 * Un view salvat tine pozitia fiecarui nod (id -> {x, y}). La deschidere harta
 * se aseaza cu `preset`, adica exact cum a lasat-o cine a salvat-o - nu cu
 * asezarea automata, care la fiecare incarcare scoate alt desen.
 *
 * NODURILE APARUTE DUPA SALVARE n-au pozitie. Nu se reaseaza toata harta pentru
 * ele - asta ar strica tocmai ce s-a salvat - ci:
 *
 *  1. un nod nou legat de unul care ARE pozitie se pune langa vecinii lui
 *     (media lor, plus un pas pe o spirala, ca doi noi langa acelasi vecin sa
 *     nu cada unul peste altul). Se repeta, deci un lant de noduri noi se
 *     aseaza din aproape in aproape;
 *  2. ce ramane - noduri noi fara niciun vecin asezat - se aseaza cu asezarea
 *     aleasa pe ecran, DOAR ele, intr-o cutie din dreapta desenului salvat.
 */

export interface Point {
  x: number
  y: number
}

export type Positions = Record<string, Point>

export interface PresetPlan {
  /** Pozitia fiecarui nod: cele salvate, plus cele puse langa vecini. */
  positions: Positions
  /** Nodurile fara niciun vecin asezat - le aseaza layout-ul ales. */
  orphans: string[]
  /** Unde stau orfanii: la dreapta a tot ce e asezat. */
  box: { x1: number; y1: number; w: number; h: number }
}

interface PlanGraph {
  nodes: { id: number | string }[]
  edges: { source: number | string; target: number | string }[]
}

/** Departarea fata de vecini la care se pune un nod nou. */
const STEP = 90

function valid(point: unknown): point is Point {
  if (typeof point !== 'object' || point === null) return false
  const p = point as Record<string, unknown>
  return typeof p.x === 'number' && typeof p.y === 'number' && Number.isFinite(p.x) && Number.isFinite(p.y)
}

/**
 * Planul de asezare cand exista pozitii salvate. `null` cand niciun nod de pe
 * harta nu are pozitie - atunci nu e nimic de pastrat, si harta se aseaza
 * normal.
 */
export function presetPlan(graph: PlanGraph, saved: Positions | null | undefined): PresetPlan | null {
  if (!saved) return null

  const ids = graph.nodes.map((n) => String(n.id))
  const positions: Positions = {}
  for (const id of ids) {
    const point = saved[id]
    if (valid(point)) positions[id] = { x: point.x, y: point.y }
  }

  if (Object.keys(positions).length === 0) return null

  const neighbours = new Map<string, string[]>()
  for (const id of ids) neighbours.set(id, [])
  for (const edge of graph.edges) {
    const a = String(edge.source)
    const b = String(edge.target)
    if (a === b || !neighbours.has(a) || !neighbours.has(b)) continue
    neighbours.get(a)!.push(b)
    neighbours.get(b)!.push(a)
  }

  /* Cati au fost pusi deja langa acelasi loc - pasul pe spirala. */
  const crowd = new Map<string, number>()

  let missing = ids.filter((id) => !(id in positions))
  for (;;) {
    const placedNow: Array<[string, Point]> = []
    for (const id of missing) {
      const anchors = neighbours.get(id)!.filter((n) => n in positions)
      if (anchors.length === 0) continue
      const cx = anchors.reduce((s, n) => s + positions[n]!.x, 0) / anchors.length
      const cy = anchors.reduce((s, n) => s + positions[n]!.y, 0) / anchors.length
      const key = anchors.slice().sort().join(',')
      const k = crowd.get(key) ?? 0
      crowd.set(key, k + 1)
      /* Unghiul de aur: punctele unei spirale nu se aliniaza niciodata. */
      const angle = k * 2.39996 + Math.PI / 4
      const radius = STEP * (1 + 0.35 * Math.sqrt(k))
      placedNow.push([id, { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius }])
    }
    if (placedNow.length === 0) break
    /* Puse dupa trecere, nu in timpul ei: ordinea din lista nu are voie sa
       hotarasca cine e ancora cui. */
    for (const [id, point] of placedNow) positions[id] = point
    missing = missing.filter((id) => !(id in positions))
  }

  const xs = Object.values(positions).map((p) => p.x)
  const ys = Object.values(positions).map((p) => p.y)
  const minY = Math.min(...ys)
  const maxX = Math.max(...xs)
  const side = Math.max(240, Math.ceil(Math.sqrt(missing.length)) * STEP * 1.5)
  const box = { x1: maxX + STEP * 2, y1: minY, w: side, h: side }

  /* Un loc de pornire pentru orfani, in grila, in cutia lor: daca layout-ul
     lor nu ruleaza (unul singur, sau biblioteca il refuza), tot stau undeva
     citet, nu toti in acelasi punct. */
  const columns = Math.max(1, Math.ceil(Math.sqrt(missing.length)))
  missing.forEach((id, i) => {
    positions[id] = { x: box.x1 + (i % columns) * STEP, y: box.y1 + Math.floor(i / columns) * STEP }
  })

  return { positions, orphans: missing, box }
}

/** Layout-ul `preset` al lui cytoscape peste un plan. */
export function presetLayout(plan: PresetPlan): object {
  return {
    name: 'preset',
    positions: (node: { id: () => string }) => plan.positions[node.id()] ?? { x: 0, y: 0 },
    fit: false,
    animate: false,
    padding: 30,
  }
}

/* Ce folosim din cytoscape aici - tipul `Cy` din draw.ts nu le numeste. */
type LayoutNode = { id: () => string; position: () => Point; isParent?: () => boolean; hasClass?: (c: string) => boolean }
type LayoutNodes = { forEach: (fn: (n: LayoutNode) => void) => void; filter: (fn: (n: LayoutNode) => boolean) => LayoutNodes; length: number; layout: (o: object) => { run: () => void } }
type LayoutCy = { nodes: () => LayoutNodes }

/**
 * Orfanii, asezati cu layout-ul ales pe ecran, numai ei, in cutia lor. Cei
 * asezati din pozitiile salvate nu se misca.
 */
export function layoutOrphans(cy: unknown, plan: PresetPlan, layout: object): void {
  if (plan.orphans.length < 2) return
  const wanted = new Set(plan.orphans)
  try {
    const nodes = (cy as LayoutCy).nodes().filter((n) => wanted.has(n.id()))
    if (nodes.length < 2) return
    nodes.layout({ ...layout, boundingBox: plan.box, fit: false, animate: false }).run()
  } catch {
    /* Raman in grila de pornire - tot citeti, doar mai putin frumosi. */
  }
}

/** Pozitiile de acum ale tuturor nodurilor, rotunjite la zecimi. */
export function readPositions(cy: unknown): Positions {
  const out: Positions = {}
  try {
    ;(cy as LayoutCy).nodes().forEach((n) => {
      /* Benzile si blocurile de fundal ale asezarii (`__arrange_*`) nu sunt
         noduri ale hartii, iar o caseta deschisa sta unde ii sunt membrii -
         niciuna nu se salveaza. O caseta restransa (`grp:...`) da. */
      if (n.id().startsWith('__arrange_') || n.hasClass?.('arrange-deco')) return
      if (n.isParent?.()) return
      const p = n.position()
      if (Number.isFinite(p.x) && Number.isFinite(p.y)) {
        out[n.id()] = { x: Math.round(p.x * 10) / 10, y: Math.round(p.y * 10) / 10 }
      }
    })
  } catch {
    /* O harta distrusa intre timp n-are pozitii. */
  }
  return out
}
